import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Paper,
  Stack,
  Card,
  useTheme,
  useMediaQuery,
  IconButton,
  Button,
  CircularProgress
} from '@mui/material';
import { keyframes } from '@emotion/react';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import DoDisturbAltIcon from '@mui/icons-material/DoDisturbAlt';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { buildWebviewWebSocketUrl, buildWebviewFetchHeaders, parseCameraIdsFromSearch, extractEntryCameraId, matchesWebviewCamera, fetchCameraTypeById, resolveCompanyLogoSrc } from '../utils/webviewAudio';
import { getAuditLogs, getAuditImageBlob } from '../utils/api';
import { getOrFetch as fetchAuditImage, peek as peekAuditImage } from '../services/auditImageCache';
import { AuditTile, getResultMeta } from './Audit';
import { AuditProcessingTimelineModal } from './AuditDialogs';

// API endpoints + websocket helpers
const normalizeBaseUrl = (value) => {
  if (!value) return '';
  return value.endsWith('/') ? value.slice(0, -1) : value;
};

const resolveApiBase = () => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    const origin = window.location.origin;
    const url = new URL(origin);
    // In production (HTTPS), use same origin (reverse proxy handles routing)
    // In development (HTTP), use port 8000 for the backend API
    if (url.protocol === 'https:') {
      return normalizeBaseUrl(origin);
    }
    // Development: backend is on port 8000
    return normalizeBaseUrl(`${url.protocol}//${url.hostname}:8000`);
  }
  return normalizeBaseUrl('http://localhost:8000');
};
const API_BASE_URL = resolveApiBase();

const deriveWebsocketUrl = (laneNumber = null, cameraIds = null) => buildWebviewWebSocketUrl(laneNumber, true, cameraIds);
// Display duration per card (9 seconds)
const DISPLAY_DURATION = 9000;
const SIDEBAR_BUCKET_LIMIT = 10;

const BLACKLISTED_STATUS_CHIP_SX = {
  bgcolor: '#1c1b1b',
  color: '#d32f2f',
  '& .MuiChip-label': {
    px: 0.75,
    color: '#ffffff',
    fontWeight: 700,
  },
};

const UNAUTHORIZED_STATUS_CHIP_SX = {
  bgcolor: '#1c1b1b',
  color: '#d32f2f',
  '& .MuiChip-label': {
    px: 0.75,
    color: '#ffffff',
    fontWeight: 700,
  },
};
const SIDEBAR_PAGE_SIZE = parseInt(
  process.env.REACT_APP_SIDEBAR_PAGE_SIZE || String(SIDEBAR_BUCKET_LIMIT),
  10,
);
const UNAUTHORIZED_SIDEBAR_PAGE_SIZE = SIDEBAR_PAGE_SIZE;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const sidebarShimmerStyles = {
  background: 'linear-gradient(90deg, rgba(255,255,255,0.08) 25%, rgba(255,255,255,0.18) 50%, rgba(255,255,255,0.08) 75%)',
  backgroundSize: '200% 100%',
  animation: `${shimmer} 1.5s ease-in-out infinite`,
};

const SidebarTileSkeleton = () => (
  <Card
    sx={{
      width: '100%',
      borderRadius: 2,
      overflow: 'hidden',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'rgba(0, 0, 0, 0.61)',
      color: '#fff',
      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.29)',
      borderTop: '3px solid transparent',
    }}
  >
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        paddingTop: '100%',
        background: '#0f172a',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'absolute', inset: 0, ...sidebarShimmerStyles }} />
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          position: 'absolute',
          bottom: 4,
          left: 4,
          right: 4,
          zIndex: 1,
        }}
      >
        <Box sx={{ width: 36, height: 18, borderRadius: 12, ...sidebarShimmerStyles }} />
        <Box sx={{ width: 64, height: 18, borderRadius: 12, ...sidebarShimmerStyles }} />
      </Stack>
    </Box>
    <Box sx={{ px: 1.5, py: 1, color: 'white', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.15 }}>
        <Box sx={{ width: '58%', height: 14, borderRadius: 1, ...sidebarShimmerStyles }} />
      </Box>
      <Box sx={{ width: '72%', height: 11, borderRadius: 1, ...sidebarShimmerStyles }} />
    </Box>
  </Card>
);

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });


const getCapturedTimestamp = (log = {}) => {
  const meta = log.request_metadata || {};
  const raw =
    log.captured_at
    || meta.captured_at
    || log.event_timestamp
    || log.created_at;
  if (raw == null || raw === '') return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return { iso: d.toISOString() };
};

const buildAuditLogFromPayload = (payload = {}, { isUnauthorized = false } = {}) => {
  const person = payload.person && typeof payload.person === 'object' ? payload.person : {};

  const parseRequestMetadata = (value) => {
    if (!value) return {};
    if (typeof value === 'object') return { ...value };
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return {};
      }
    }
    return {};
  };

  const requestMetadataKeys = [
    'captured_at', 'captured_at_ms', 'event_time', 'event_time_ms', 'event_timestamp', 'event_timestamp_ms',
    'api_submit_at', 'api_submit_at_ms', 'api_received_at', 'api_received_at_ms',
    'api_completed_at', 'api_completed_at_ms', 'recognize_started_at', 'recognize_started_at_ms',
    'matching_started_at', 'matching_started_at_ms', 'ingested_at', 'ingested_at_ms',
    'lane_number', 'number_of_lane', 'face_center_x', 'top_candidates', 'matched_angle', 'query_angle',
    'send_attendance_for_possible_match', 'timing_frame_prep_ms', 'timing_detection_ms',
    'timing_queue_prep_ms', 'timing_queue_wait_ms', 'timing_capture_to_worker_ms',
    'detection_confidence', 'face_detection_width', 'face_detection_height', 'face_det_width_height',
    'movement_direction', 'tag',
  ];

  const requestMetadata = parseRequestMetadata(payload.request_metadata);
  requestMetadataKeys.forEach((key) => {
    if (payload[key] != null && requestMetadata[key] == null) {
      requestMetadata[key] = payload[key];
    }
  });
  if (
    requestMetadata.face_detection_width != null
    && requestMetadata.face_detection_height != null
    && !requestMetadata.face_det_width_height
  ) {
    requestMetadata.face_det_width_height = `${requestMetadata.face_detection_width} x ${requestMetadata.face_detection_height}`;
  }

  const confidenceScore = payload.confidence_score ?? payload.confidence ?? null;
  const confidenceLevel = isUnauthorized
    ? (payload.confidence_level || 'no_match')
    : payload.confidence_level;

  return {
    ...payload,
    audit_id: payload.audit_id || payload.id,
    recognized_name:
      payload.recognized_name
      || payload.person_name
      || payload.name
      || person.name
      || (isUnauthorized ? 'Unknown Person' : undefined),
    recognized_person_id: isUnauthorized
      ? null
      : (
        payload.recognized_person_id
        || payload.person_id
        || person.person_id
      ),
    camera: payload.camera,
    track_id: payload.track_id ?? payload.trackId,
    employee_type: payload.employee_type ?? person.employee_type,
    captured_at:
      payload.captured_at
      || payload.timestamp
      || payload.created_at
      || payload.event_timestamp
      || requestMetadata.captured_at,
    created_at: payload.created_at || payload.captured_at || payload.timestamp,
    captured_at_ms: payload.captured_at_ms ?? requestMetadata.captured_at_ms,
    success: isUnauthorized ? false : payload.success !== false,
    confidence_level: confidenceLevel,
    confidence_score: confidenceScore,
    operation_type: payload.operation_type || 'recognize',
    processing_time_ms: payload.processing_time_ms,
    is_intrusion: isUnauthorized ? false : Boolean(payload.is_intrusion),
    request_metadata: requestMetadata,
    api_submit_at: payload.api_submit_at ?? requestMetadata.api_submit_at,
    api_submit_at_ms: payload.api_submit_at_ms ?? requestMetadata.api_submit_at_ms,
    api_received_at: payload.api_received_at ?? requestMetadata.api_received_at,
    api_received_at_ms: payload.api_received_at_ms ?? requestMetadata.api_received_at_ms,
    api_completed_at: payload.api_completed_at ?? requestMetadata.api_completed_at,
    api_completed_at_ms: payload.api_completed_at_ms ?? requestMetadata.api_completed_at_ms,
    recognize_started_at: payload.recognize_started_at ?? requestMetadata.recognize_started_at,
    recognize_started_at_ms: payload.recognize_started_at_ms ?? requestMetadata.recognize_started_at_ms,
    matching_started_at: payload.matching_started_at ?? requestMetadata.matching_started_at,
    matching_started_at_ms: payload.matching_started_at_ms ?? requestMetadata.matching_started_at_ms,
  };
};

const GENERIC_SIDEBAR_NAMES = new Set([
  'unknown person',
  'unknown visitor',
  'unknown',
  'unauthorized person',
  'recognition frame',
]);

const isGenericSidebarName = (name) => {
  const normalized = String(name || '').trim().toLowerCase();
  return !normalized || GENERIC_SIDEBAR_NAMES.has(normalized);
};

const entryToAuditLog = (entry = {}) => {
  const base = entry.auditLog
    ? { ...entry.auditLog }
    : buildAuditLogFromPayload(
      {
        audit_id: entry.auditId || entry.id,
        recognized_name: entry.name,
        recognized_person_id: entry.personId,
        camera: entry.camera,
        track_id: entry.trackId,
        employee_type: entry.employeeType,
        captured_at: entry.timestamp,
        created_at: entry.timestamp,
      },
      { isUnauthorized: entry.isUnauthorized }
    );

  if (entry.isUnauthorized) {
    base.success = false;
    base.confidence_level = base.confidence_level || 'no_match';
    base.recognized_person_id = null;
    base.employee_code = null;
    if (!base.recognized_name || base.recognized_name === 'Unauthorized Person') {
      base.recognized_name = 'Unknown Person';
    }
  } else {
    base.success = base.success !== false;
    base.is_intrusion = base.is_intrusion !== false;
    if (isGenericSidebarName(base.recognized_name)) {
      const resolvedName = entry.name || base.trained_name;
      if (!isGenericSidebarName(resolvedName)) {
        base.recognized_name = resolvedName;
      }
    }
  }

  return base;
};

const mapAuditLogToDisplayPayload = (log = {}) => {
  const camera = (log.camera || '').toString().toUpperCase();
  const action = camera === 'OUT' ? 'check_out' : 'check_in';
  const cameraDetail = log.camera_detail && typeof log.camera_detail === 'object'
    ? log.camera_detail
    : {};
  return {
    audit_id: log.audit_id,
    id: log.audit_id,
    recognized_name: log.recognized_name,
    recognized_person_id: log.recognized_person_id,
    employee_code: log.employee_code,
    camera: log.camera,
    camera_id: log.camera_id || log.cameraId || cameraDetail.camera_id || cameraDetail.cameraId,
    lane_number: log.lane_number,
    track_id: log.track_id,
    employee_type: log.employee_type,
    action,
    mode: 'attendance',
    event: 'attendance',
    timestamp: log.captured_at || log.created_at || log.event_timestamp,
    created_at: log.created_at,
    captured_at: log.captured_at,
    is_intrusion: true,
  };
};

const mapUnauthorizedAuditLogToDisplayPayload = (log = {}) => {
  const camera = (log.camera || '').toString().toUpperCase();
  const action = camera === 'OUT' ? 'check_out' : 'check_in';
  const cameraDetail = log.camera_detail && typeof log.camera_detail === 'object'
    ? log.camera_detail
    : {};
  return {
    audit_id: log.audit_id,
    id: log.audit_id,
    recognized_name: 'Unknown Person',
    person_name: 'Unknown Person',
    camera: log.camera,
    camera_id: log.camera_id || log.cameraId || cameraDetail.camera_id || cameraDetail.cameraId,
    lane_number: log.lane_number,
    track_id: log.track_id,
    action,
    mode: 'attendance',
    event: 'recognition',
    event_status: 'unmatched',
    timestamp: log.captured_at || log.created_at || log.event_timestamp,
    created_at: log.created_at,
    captured_at: log.captured_at,
  };
};

const MAX_STACK_PREVIEW = 3;

const getStackLogKey = (log) => {
  if (!log) return null;
  if (log.audit_id) return `audit:${String(log.audit_id)}`;
  const timestamp = log.captured_at || log.created_at || '';
  const camera = (log.camera || '').toString().toLowerCase();
  return `fallback:${String(log.track_id || 'track')}:${camera}:${String(timestamp)}`;
};

const buildSidebarStackImages = (bucketLogs = [], representativeLog = null, imageCache = {}) => {
  const logs = Array.isArray(bucketLogs) ? bucketLogs : [];
  const orderedLogs = [];
  const seen = new Map();

  const addLog = (log) => {
    const key = getStackLogKey(log);
    if (!key) return;
    if (seen.has(key)) {
      const existingIndex = seen.get(key);
      orderedLogs[existingIndex] = { ...orderedLogs[existingIndex], ...log };
      return;
    }
    seen.set(key, orderedLogs.length);
    orderedLogs.push(log);
  };

  addLog(representativeLog);
  logs.forEach(addLog);

  return orderedLogs.slice(0, MAX_STACK_PREVIEW).map((stackLog) => ({
    log: stackLog,
    image: stackLog?.audit_id ? imageCache[stackLog.audit_id] : null,
  }));
};

const collectBucketAuditIds = (entry) => {
  const ids = new Set();
  const pushId = (value) => {
    if (value) ids.add(String(value));
  };
  pushId(entry?.auditId);
  pushId(entry?.auditLog?.audit_id);
  (entry?.bucketLogs || []).forEach((log) => pushId(log?.audit_id));
  (entry?.auditLog?.bucket_logs || []).forEach((log) => pushId(log?.audit_id));
  return Array.from(ids);
};

const pad2 = (value) => value.toString().padStart(2, '0');

const normalizeTimestampString = (value) => {
  if (!value) return null;
  let normalized = value.trim();
  if (!normalized) return null;
  // Replace space between date/time with T for ISO parsing
  if (normalized.includes(' ') && !normalized.includes('T')) {
    normalized = normalized.replace(' ', 'T');
  }
  // Ensure timezone info present; default to UTC if missing
  if (!/(z|Z|[+-]\d{2}:?\d{2})$/.test(normalized)) {
    normalized = `${normalized}${normalized.includes('T') ? '' : 'T'}${normalized.includes('T') ? '' : '00:00:00'}Z`;
  }
  // Trim microseconds to milliseconds for Date parsing
  const dotIndex = normalized.indexOf('.');
  if (dotIndex !== -1) {
    const zoneMatch = normalized.slice(dotIndex).match(/(Z|[+-]\d{2}:?\d{2})$/);
    const zone = zoneMatch ? zoneMatch[0] : '';
    const fraction = normalized.slice(dotIndex + 1, zoneMatch ? -zone.length : undefined);
    const millis = fraction.slice(0, 3).padEnd(3, '0');
    const beforeDot = normalized.slice(0, dotIndex);
    normalized = `${beforeDot}.${millis}${zone}`;
  }
  return normalized;
};

const parseTimestampValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    const date = new Date(value > 1e12 ? value : value * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^\d{10,}$/.test(trimmed)) {
      const num = Number(trimmed);
      const date = new Date(num > 1e12 ? num : num * 1000);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    const normalized = normalizeTimestampString(trimmed);
    if (!normalized) return null;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const formatTimestampLabel = (rawValue, fallbackDate) => {
  const parsed = parseTimestampValue(rawValue) || fallbackDate;
  if (!(parsed instanceof Date) || Number.isNaN(parsed.getTime())) {
    return '';
  }
  // Date Parts
  const day = pad2(parsed.getDate());
  const month = pad2(parsed.getMonth() + 1);
  const year = parsed.getFullYear();

  // Time Parts (12-hour)
  const hours24 = parsed.getHours();
  const period = hours24 >= 12 ? 'PM' : 'AM';
  let hours12 = hours24 % 12;
  if (hours12 === 0) hours12 = 12;
  const hours = pad2(hours12);
  const minutes = pad2(parsed.getMinutes());

  // Final Format: TIME FIRST, THEN DATE
  return `${hours}:${minutes} ${period} · ${day}/${month}/${year}`;
};

// Animations
const float = keyframes`
  0%, 100% { transform: translate(0px, 0px) rotate(0deg); }
  33% { transform: translate(30px, -30px) rotate(120deg); }
  66% { transform: translate(-20px, 20px) rotate(240deg); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.03); }
  100% { transform: scale(1); }
`;

const connectionPulse = keyframes`
  0% { box-shadow: 0 0 0 0 currentColor; }
  70% { box-shadow: 0 0 0 8px rgba(0, 0, 0, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0); }
`;

// Fallback avatar component
const toDataUrl = (value) => {
  if (!value) return null;
  if (value.startsWith('data:')) return value;
  return `data:image/jpeg;base64,${value}`;
};

const toLaneNumber = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  const parsed = parseInt(String(raw), 10);
  return Number.isNaN(parsed) || parsed <= 0 ? null : parsed;
};

const resolveLaneFromPathname = () => {
  if (typeof window === 'undefined' || !window.location) return null;
  const parts = String(window.location.pathname || '').split('/').filter(Boolean);
  const webviewIdx = parts.findIndex((p) => {
    const lower = String(p).toLowerCase();
    return lower === 'webview-intrusion' || lower === 'webview';
  });
  if (webviewIdx < 0 || webviewIdx + 1 >= parts.length) return null;
  const rawPart = String(parts[webviewIdx + 1] || '');
  let decoded = rawPart;
  try {
    decoded = decodeURIComponent(rawPart);
  } catch (e) {
    decoded = rawPart;
  }
  const compact = decoded.toLowerCase().trim().replace(/\s+/g, '');
  const match = compact.match(/^lane(\d+)$/) || compact.match(/^lane-(\d+)$/) || compact.match(/^(\d+)$/);
  return match ? toLaneNumber(match[1]) : null;
};

const WebViewIntrusion = ({ laneNumber = null, embedded = false }) => {
  const location = useLocation();
  // preserved states
  const [currentEntry, setCurrentEntry] = useState(null);
  const [sidebarEntries, setSidebarEntries] = useState([]);
  const [unauthorizedSidebarEntries, setUnauthorizedSidebarEntries] = useState([]);

  // active main-screen cards (FIFO). newest at end.
  const [activeCards, setActiveCards] = useState([]);

  // Company settings state - fetched from database (active company with is_active=true)
  const [companySettings, setCompanySettings] = useState({
    logo: '/assets/icons/kivo-fav-icon.png', // Default logo
    description: 'Welcome to Kivo.ai' // Default message
  });
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [cameraTypeById, setCameraTypeById] = useState({});
  const [hasMoreLogs, setHasMoreLogs] = useState(false);
  const [loadingMoreLogs, setLoadingMoreLogs] = useState(false);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [unauthorizedHasMoreLogs, setUnauthorizedHasMoreLogs] = useState(false);
  const [unauthorizedLoadingMoreLogs, setUnauthorizedLoadingMoreLogs] = useState(false);
  const [unauthorizedHistoryOffset, setUnauthorizedHistoryOffset] = useState(0);
  const [initialBlacklistedLogsLoading, setInitialBlacklistedLogsLoading] = useState(true);
  const [initialUnauthorizedLogsLoading, setInitialUnauthorizedLogsLoading] = useState(true);
  const [blacklistedVisibleLimit, setBlacklistedVisibleLimit] = useState(SIDEBAR_PAGE_SIZE);
  const [unauthorizedVisibleLimit, setUnauthorizedVisibleLimit] = useState(UNAUTHORIZED_SIDEBAR_PAGE_SIZE);
  const [timingDialog, setTimingDialog] = useState({ open: false, log: null, stack: [] });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const websocketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const dedupeTimersRef = useRef(new Map());
  const snapshotRequestsRef = useRef(new Set());
  const unauthorizedSnapshotRequestsRef = useRef(new Set());

  // Map of per-card timers: id -> timeoutId
  const activeTimersRef = useRef(new Map());

  const resolvedLaneNumber = useMemo(() => {
    const fromProp = toLaneNumber(laneNumber);
    if (fromProp) return fromProp;
    return resolveLaneFromPathname();
  }, [laneNumber]);

  const resolvedCameraIds = useMemo(
    () => parseCameraIdsFromSearch(location?.search || ''),
    [location?.search]
  );
  const cameraFilterKey = resolvedCameraIds.join(',');

  useEffect(() => {
    if (!resolvedCameraIds.length) {
      setCameraTypeById({});
      return;
    }
    let cancelled = false;
    fetchCameraTypeById(API_BASE_URL)
      .then((map) => {
        if (!cancelled) {
          console.log('[CUSTOM-WEBVIEW] camera types', map);
          setCameraTypeById(map);
        }
      })
      .catch((err) => console.error('[CUSTOM-WEBVIEW] camera type load failed', err));
    return () => {
      cancelled = true;
    };
  }, [cameraFilterKey, resolvedCameraIds.length]);

  const matchesLane = useCallback((entry) => {
    if (!resolvedLaneNumber) return true;
    return toLaneNumber(entry?.laneNumber) === resolvedLaneNumber;
  }, [resolvedLaneNumber]);

  const matchesCamera = useCallback((entry) => (
    matchesWebviewCamera(entry, resolvedCameraIds, cameraTypeById)
  ), [cameraTypeById, resolvedCameraIds]);

  // helper to determine attendance event
  const isAttendanceEvent = (payload = {}) => {
    const eventType = (payload.event || '').toString().toLowerCase();
    const mode = (payload.mode || '').toString().toLowerCase();
    return eventType === 'attendance' || mode === 'attendance';
  };

  const isUnauthorizedRecognition = (payload = {}) => {
    const eventType = String(payload.event || '').toLowerCase();
    const mode = String(payload.mode || '').toLowerCase();
    const status = String(payload.event_status || '').toLowerCase();
    return (
      (eventType === 'recognition' || mode === 'attendance')
      && (status === 'unmatched' || status === 'no_match')
    );
  };

  const isMatchedIntrusionEvent = (payload = {}) => {
    if (isUnauthorizedRecognition(payload)) return false;
    if (!isAttendanceEvent(payload)) return false;
    return Boolean(payload.is_intrusion);
  };

  const buildDisplayEntry = (payload = {}, overrides = {}) => {
    const person = payload.person || {};
    const timestampValue = overrides.timestamp || payload.timestamp || payload.captured_at || payload.created_at || payload.event_timestamp;
    const parsedTimestamp = parseTimestampValue(timestampValue);
    const safeDate = parsedTimestamp || new Date();
    const rawAction = (overrides.action || payload.action || payload.type || '').toString().toLowerCase();
    const action = rawAction.includes('out') ? 'check_out' : 'check_in';
    const rawMode = (overrides.mode || payload.mode || (payload.event === 'attendance' ? 'attendance' : 'normal') || '').toString().toLowerCase();
    const mode = rawMode === 'attendance' ? 'attendance' : 'normal';
    const camera = (overrides.camera || payload.camera || (action === 'check_out' ? 'OUT' : 'IN') || '').toString().toUpperCase();
    const trackId = overrides.trackId || payload.track_id || payload.trackId || payload.trackID || payload.trackid;
    const name = overrides.name || payload.recognized_name || payload.person_name || payload.name || person.name || 'Unknown Visitor';
    const isUnauthorizedEntry = Boolean(
      overrides.isUnauthorized ?? payload.is_unauthorized ?? payload.isUnauthorized
    );
    const personId = isUnauthorizedEntry
      ? null
      : (overrides.personId || payload.recognized_person_id || payload.person_id || payload.id || person.person_id || `guest-${Date.now()}`);
    const auditId = overrides.auditId || payload.audit_id || null;
    const employeeCode = overrides.employee_code || payload.employee_code || person.employee_code || null;
    const department = overrides.department || payload.department || person.department || 'General';
    const employeeType =
      overrides.employeeType ?? payload.employee_type ?? person.employee_type ?? null;
    const isAttendancePayload =
      (payload.event || '').toString().toLowerCase() === 'attendance'
      || rawMode === 'attendance';
    const profileImage = toDataUrl(
      overrides.profileImage
      || payload.profile_image
      || (!isAttendancePayload ? (payload.image || person.image) : null)
      || null
    );
    const capturedImage = toDataUrl(
      overrides.capturedImage
      || payload.captured_image
      || (isAttendancePayload ? payload.image : null)
      || null
    );
    const image = capturedImage || profileImage;
    

    const metadata = payload.request_metadata && typeof payload.request_metadata === 'object'
      ? payload.request_metadata
      : {};
    const entryLaneNumber = toLaneNumber(
      overrides.laneNumber ??
      payload.lane_number ??
      payload.laneNumber ??
      metadata.lane_number ??
      metadata.laneNumber
    );
    const entryCameraId = extractEntryCameraId(payload, overrides);

    return {
      id: auditId || payload.id || payload.attendance_id || `${personId}-${safeDate.getTime()}`,
      auditId,
      isUnauthorized: Boolean(overrides.isUnauthorized ?? payload.is_unauthorized ?? payload.isUnauthorized),
      name,
      personId,
      employeeCode,
      trackId,
      action,
      mode,
      camera,
      image,
      profileImage,
      capturedImage,
      department,
      employeeType,
      timestamp: safeDate.toISOString(),
      time: formatTimestampLabel(timestampValue, safeDate),
      modeLabel: mode === 'attendance' ? 'Attendance Marked' : 'Normal Check-In',
      directionText: camera ? `${camera} Camera` : (action === 'check_out' ? 'Exit Event' : 'Entry Event'),
      laneNumber: entryLaneNumber,
      cameraId: entryCameraId,
      auditLog: overrides.auditLog ?? payload.auditLog ?? null,
    };
  };

  const pushEntryToSidebar = useCallback((entry) => {
    if (!entry) return;

    setSidebarEntries(current => {
      // Remove duplicate with the same ID
      const filtered = current.filter(e => e.id !== entry.id);

      // Add new notification at top
      return [entry, ...filtered];
    });
  }, []);

  const pushEntryToUnauthorizedSidebar = useCallback((entry) => {
    if (!entry) return;

    setUnauthorizedSidebarEntries((current) => {
      const filtered = current.filter((e) => e.id !== entry.id);
      return [entry, ...filtered];
    });
  }, []);

  const pushEntryToHistorySidebar = useCallback((entry) => {
    if (!entry) return;
    if (entry.isUnauthorized) {
      pushEntryToUnauthorizedSidebar(entry);
      return;
    }
    pushEntryToSidebar(entry);
  }, [pushEntryToSidebar, pushEntryToUnauthorizedSidebar]);

  const getEntryKey = (entry) => {
    if (!entry) return null;
    if (entry.auditId) {
      return `audit-${entry.auditId}`;
    }
    if (entry.trackId !== undefined && entry.trackId !== null) {
      return `track-${entry.trackId}`;
    }
    if (entry.personId) {
      return `${entry.personId}-${entry.action}`;
    }
    return null;
  };

  // dedupe logic preserved
  const shouldDisplayEntry = (entry) => {
    const key = getEntryKey(entry);
    if (!key) return true;
    const existing = dedupeTimersRef.current.get(key);
    if (existing) {
      return false;
    }
    const timeoutId = setTimeout(() => {
      dedupeTimersRef.current.delete(key);
    }, DISPLAY_DURATION);
    dedupeTimersRef.current.set(key, timeoutId);
    return true;
  };

  // remove card helper (called on expiry or when popping due to >4)
  const removeCard = useCallback((id) => {
    setActiveCards(prev => {
      const card = prev.find(c => c.id === id);
      const filtered = prev.filter(c => c.id !== id);
      // push that card to sidebar as notification
      if (card) {
        pushEntryToHistorySidebar(card.original || card);
      }
      // clear timer map
      const t = activeTimersRef.current.get(id);
      if (t) {
        clearTimeout(t);
      }
      activeTimersRef.current.delete(id);
      return filtered;
    });
  }, [pushEntryToHistorySidebar]);

  // add or refresh card in activeCards (with its own timer)
  const addOrRefreshCard = useCallback((entry) => {
    if (!entry) return;
    setActiveCards(prev => {
      // check if already present
      const existing = prev.find(c => c.id === entry.id);
      if (existing) {
        // refresh timer: clear old timer and set a new one
        const oldTimer = activeTimersRef.current.get(entry.id);
        if (oldTimer) clearTimeout(oldTimer);
        const newTimeout = setTimeout(() => {
          removeCard(entry.id);
        }, DISPLAY_DURATION);
        activeTimersRef.current.set(entry.id, newTimeout);
        // update addedAt timestamp
        return prev.map(c => c.id === entry.id ? ({ ...c, addedAt: Date.now(), original: entry }) : c);
      }

      // Not present: if already 4 cards, remove the oldest (FIFO) and send to sidebar
      let next = [...prev];
      if (next.length >= 4) {
        const oldest = next.shift(); // remove first
        if (oldest) {
          // clear its timer and push to sidebar
          const oldT = activeTimersRef.current.get(oldest.id);
          if (oldT) clearTimeout(oldT);
          activeTimersRef.current.delete(oldest.id);
          pushEntryToHistorySidebar(oldest.original || oldest);
        }
      }

      // add new card to the end (so newest at end)
      const wrapped = { ...entry, addedAt: Date.now(), original: entry };
      next.push(wrapped);

      // set timer for this card
      const timeoutId = setTimeout(() => {
        removeCard(wrapped.id);
      }, DISPLAY_DURATION);
      activeTimersRef.current.set(wrapped.id, timeoutId);

      return next;
    });
  }, [pushEntryToHistorySidebar, removeCard]);

  // showEntry replaced/augmented to manage activeCards rather than single currentEntry only
  const showEntry = (entry) => {
    if (!entry) return;
    if (!shouldDisplayEntry(entry)) {
      return;
    }
    // add or refresh active card
    addOrRefreshCard(entry);
  };

  // whenever activeCards changes, keep currentEntry in-sync with the newest (last) card for backward compatibility
  useEffect(() => {
    if (activeCards.length === 0) {
      setCurrentEntry(null);
    } else {
      // show the latest added card as "currentEntry"
      const last = activeCards[activeCards.length - 1];
      setCurrentEntry(last.original || last);
    }
  }, [activeCards]);

  // cleanup timers on unmount
  useEffect(() => {
    return () => {
      // clear all active timers
      activeTimersRef.current.forEach(t => clearTimeout(t));
      activeTimersRef.current.clear();
      // clear dedupe timers
      dedupeTimersRef.current.forEach(t => clearTimeout(t));
      dedupeTimersRef.current.clear();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (websocketRef.current) {
        try { websocketRef.current.close(); } catch (e) { }
        websocketRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mapBucketedAuditLogToSidebarEntry = useCallback((log = {}, { isUnauthorized = false } = {}) => {
    const bucketLogs = Array.isArray(log.bucket_logs) && log.bucket_logs.length > 0
      ? log.bucket_logs
      : [log];
    const auditLog = isUnauthorized
      ? buildAuditLogFromPayload(log, { isUnauthorized: true })
      : log;
    const payload = isUnauthorized
      ? mapUnauthorizedAuditLogToDisplayPayload(log)
      : mapAuditLogToDisplayPayload(log);
    const entry = buildDisplayEntry(payload, {
      mode: 'attendance',
      name: isUnauthorized ? 'Unknown Person' : undefined,
      isUnauthorized,
      auditLog,
    });

    return {
      ...entry,
      id: log.bucket_key || entry.id,
      bucketKey: log.bucket_key || null,
      stackSize: log.stack_size || bucketLogs.length,
      bucketLogs: bucketLogs.map((bucketLog) => (
        isUnauthorized
          ? buildAuditLogFromPayload(bucketLog, { isUnauthorized: true })
          : bucketLog
      )),
      auditLog: {
        ...auditLog,
        stack_size: log.stack_size || bucketLogs.length,
        bucket_logs: bucketLogs,
        bucket_key: log.bucket_key,
        bucket_status: log.bucket_status,
      },
    };
  }, []);

  const formatAuditLogsForSidebar = useCallback((logs = []) => (
    logs
      .map((log) => mapBucketedAuditLogToSidebarEntry(log, { isUnauthorized: false }))
      .filter(matchesLane)
      .filter(matchesCamera)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
  ), [mapBucketedAuditLogToSidebarEntry, matchesCamera, matchesLane]);

  const formatUnauthorizedAuditLogsForSidebar = useCallback((logs = []) => (
    logs
      .map((log) => mapBucketedAuditLogToSidebarEntry(log, { isUnauthorized: true }))
      .filter(matchesLane)
      .filter(matchesCamera)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
  ), [mapBucketedAuditLogToSidebarEntry, matchesCamera, matchesLane]);

  const mergeSidebarHistory = useCallback((existing, incoming) => {
    const existingIds = new Set(existing.map((entry) => entry.id));
    const dedupedIncoming = incoming.filter((entry) => !existingIds.has(entry.id));
    return [...existing, ...dedupedIncoming];
  }, []);

  // Load recent intrusion audit logs into sidebar history on mount
  useEffect(() => {
    let isMounted = true;
    const loadInitialLogs = async () => {
      setInitialBlacklistedLogsLoading(true);
      try {
        const res = await getAuditLogs({
          is_intrusion: true,
          status: 'match',
          bucketize: true,
          days: 0,
          limit: SIDEBAR_PAGE_SIZE,
          offset: 0,
          camera_id: resolvedCameraIds,
        });
        const logs = res.logs || [];
        console.log('[CUSTOM-WEBVIEW] intrusion history', {
          raw: logs.length,
          filterIds: resolvedCameraIds,
          rawCameraIds: logs.map((log) => log.camera_id || log.cameraId || null),
        });
        if (!isMounted) return;
        setSidebarEntries(formatAuditLogsForSidebar(logs));
        setHistoryOffset(logs.length);
        setHasMoreLogs(
          logs.length > 0 && (res.pagination?.has_next ?? logs.length >= SIDEBAR_PAGE_SIZE)
        );
      } catch (error) {
        console.error('Error fetching intrusion audit logs:', error);
        if (isMounted) {
          setHasMoreLogs(false);
        }
      } finally {
        if (isMounted) {
          setInitialBlacklistedLogsLoading(false);
        }
      }
    };

    setHistoryOffset(0);
    setHasMoreLogs(false);
    loadInitialLogs();

    return () => {
      isMounted = false;
    };
  }, [cameraFilterKey, formatAuditLogsForSidebar, matchesLane]);

  // Load recent unauthorized (unmatch) audit logs into sidebar history on mount
  useEffect(() => {
    let isMounted = true;
    const loadInitialUnauthorizedLogs = async () => {
      setInitialUnauthorizedLogsLoading(true);
      try {
        const res = await getAuditLogs({
          status: 'unmatch',
          bucketize: true,
          days: 0,
          limit: UNAUTHORIZED_SIDEBAR_PAGE_SIZE,
          offset: 0,
          camera_id: resolvedCameraIds,
        });
        const logs = res.logs || [];
        if (!isMounted) return;
        setUnauthorizedSidebarEntries(formatUnauthorizedAuditLogsForSidebar(logs));
        setUnauthorizedHistoryOffset(logs.length);
        setUnauthorizedHasMoreLogs(
          logs.length > 0 && (res.pagination?.has_next ?? logs.length >= UNAUTHORIZED_SIDEBAR_PAGE_SIZE)
        );
      } catch (error) {
        console.error('Error fetching unauthorized audit logs:', error);
        if (isMounted) {
          setUnauthorizedHasMoreLogs(false);
        }
      } finally {
        if (isMounted) {
          setInitialUnauthorizedLogsLoading(false);
        }
      }
    };

    setUnauthorizedHistoryOffset(0);
    setUnauthorizedHasMoreLogs(false);
    loadInitialUnauthorizedLogs();

    return () => {
      isMounted = false;
    };
  }, [cameraFilterKey, formatUnauthorizedAuditLogsForSidebar, matchesLane]);

  const loadMoreLogs = useCallback(async () => {
    if (loadingMoreLogs || !hasMoreLogs) return;
    setLoadingMoreLogs(true);
    try {
      const res = await getAuditLogs({
        is_intrusion: true,
        status: 'match',
        bucketize: true,
        days: 0,
        limit: SIDEBAR_PAGE_SIZE,
        offset: historyOffset,
        camera_id: resolvedCameraIds,
      });
      const logs = res.logs || [];
      const formatted = formatAuditLogsForSidebar(logs);
      setSidebarEntries((prev) => mergeSidebarHistory(prev, formatted));
      setHistoryOffset((prev) => prev + logs.length);
      setHasMoreLogs(
        logs.length > 0 && (res.pagination?.has_next ?? logs.length >= SIDEBAR_PAGE_SIZE)
      );
    } catch (error) {
      console.error('Error loading more intrusion audit logs:', error);
    } finally {
      setLoadingMoreLogs(false);
    }
  }, [
    formatAuditLogsForSidebar,
    hasMoreLogs,
    historyOffset,
    loadingMoreLogs,
    mergeSidebarHistory,
    cameraFilterKey,
    resolvedCameraIds,
  ]);

  const loadMoreUnauthorizedLogs = useCallback(async () => {
    if (unauthorizedLoadingMoreLogs || !unauthorizedHasMoreLogs) return;
    setUnauthorizedLoadingMoreLogs(true);
    try {
      const res = await getAuditLogs({
        status: 'unmatch',
        bucketize: true,
        days: 0,
        limit: UNAUTHORIZED_SIDEBAR_PAGE_SIZE,
        offset: unauthorizedHistoryOffset,
        camera_id: resolvedCameraIds,
      });
      const logs = res.logs || [];
      const formatted = formatUnauthorizedAuditLogsForSidebar(logs);
      setUnauthorizedSidebarEntries((prev) => mergeSidebarHistory(prev, formatted));
      setUnauthorizedHistoryOffset((prev) => prev + logs.length);
      setUnauthorizedHasMoreLogs(
        logs.length > 0 && (res.pagination?.has_next ?? logs.length >= UNAUTHORIZED_SIDEBAR_PAGE_SIZE)
      );
    } catch (error) {
      console.error('Error loading more unauthorized audit logs:', error);
    } finally {
      setUnauthorizedLoadingMoreLogs(false);
    }
  }, [
    formatUnauthorizedAuditLogsForSidebar,
    unauthorizedHasMoreLogs,
    unauthorizedHistoryOffset,
    unauthorizedLoadingMoreLogs,
    mergeSidebarHistory,
    cameraFilterKey,
    resolvedCameraIds,
  ]);

  const handleLoadMoreBlacklisted = useCallback(async () => {
    setBlacklistedVisibleLimit((prev) => prev + SIDEBAR_PAGE_SIZE);
    await loadMoreLogs();
  }, [loadMoreLogs]);

  const handleLoadMoreUnauthorized = useCallback(async () => {
    setUnauthorizedVisibleLimit((prev) => prev + UNAUTHORIZED_SIDEBAR_PAGE_SIZE);
    await loadMoreUnauthorizedLogs();
  }, [loadMoreUnauthorizedLogs]);

  const [auditImageCache, setAuditImageCache] = useState({});

  const imageCache = useMemo(() => {
    const cache = { ...auditImageCache };
    const allEntries = [
      ...sidebarEntries,
      ...unauthorizedSidebarEntries,
      ...activeCards.map((card) => card.original || card),
    ];
    allEntries.forEach((entry) => {
      collectBucketAuditIds(entry).forEach((auditId) => {
        const image = entry?.capturedImage || entry?.image;
        if (auditId && image && !cache[auditId]) {
          cache[auditId] = image;
        }
      });
    });
    return cache;
  }, [auditImageCache, sidebarEntries, unauthorizedSidebarEntries, activeCards]);

  const isBlacklistedTimingDialog = Boolean(
    timingDialog.log?.is_intrusion && timingDialog.log?.success
  );
  const isUnauthorizedTimingDialog = Boolean(
    timingDialog.log
    && !timingDialog.log.is_intrusion
    && (timingDialog.log.confidence_level === 'no_match' || !timingDialog.log.success)
  );

  const timingDialogResultMeta = useMemo(() => {
    const log = timingDialog.log;
    if (!log) return { label: 'Unknown', color: 'default' };
    if (log.is_intrusion && log.success) {
      return {
        label: 'Blacklisted',
        color: 'default',
        chipSx: BLACKLISTED_STATUS_CHIP_SX,
      };
    }
    if (!log.is_intrusion && (log.confidence_level === 'no_match' || !log.success)) {
      return {
        label: 'Unmatched',
        color: 'default',
        chipSx: UNAUTHORIZED_STATUS_CHIP_SX,
      };
    }
    return getResultMeta(log);
  }, [timingDialog.log]);

  const openEntryTimingDialog = useCallback((entry) => {
    if (!entry) return;
    const log = entryToAuditLog(entry);
    const bucketLogs = entry.bucketLogs || log.bucket_logs || [];
    const stackImages = buildSidebarStackImages(bucketLogs, log, imageCache);
    const stack = stackImages.length > 0
      ? stackImages
      : [{
        log,
        image: entry.capturedImage || entry.image || (log.audit_id ? imageCache[log.audit_id] : null) || null,
      }];
    setTimingDialog({
      open: true,
      log: {
        ...log,
        stack_size: entry.stackSize || log.stack_size || stack.length,
        bucket_logs: bucketLogs,
        bucket_key: entry.bucketKey || log.bucket_key,
      },
      stack,
    });
  }, [imageCache]);

  const closeTimingDialog = useCallback(() => {
    setTimingDialog({ open: false, log: null, stack: [] });
  }, []);

  const loadBucketAuditImages = useCallback((entries, requestsRef, updateEntries) => {
    entries.forEach((entry) => {
      const auditIds = collectBucketAuditIds(entry).filter((auditId) => peekAuditImage(auditId) === undefined);
      auditIds.forEach((auditId) => {
        if (!auditId || requestsRef.current.has(auditId)) return;
        requestsRef.current.add(auditId);

        (async () => {
          try {
            const objectUrl = await fetchAuditImage(auditId);
            if (!objectUrl) return;
            setAuditImageCache((prev) => (
              prev[auditId] ? prev : { ...prev, [auditId]: objectUrl }
            ));
            updateEntries((prev) =>
              prev.map((e) => {
                const entryAuditIds = collectBucketAuditIds(e);
                if (!entryAuditIds.includes(auditId)) return e;
                const representativeAuditId = e.auditId || e.auditLog?.audit_id;
                const shouldSetRepresentativeImage = representativeAuditId === auditId || !e.capturedImage;
                return {
                  ...e,
                  ...(shouldSetRepresentativeImage
                    ? { capturedImage: e.capturedImage || objectUrl, image: e.image || objectUrl }
                    : {}),
                };
              })
            );
          } catch (err) {
            console.error('Failed to load intrusion snapshot:', auditId, err);
          } finally {
            requestsRef.current.delete(auditId);
          }
        })();
      });
    });
  }, []);

  // Lazy-load snapshot images for historical sidebar entries (including bucket frames)
  useEffect(() => {
    loadBucketAuditImages(
      sidebarEntries,
      snapshotRequestsRef,
      setSidebarEntries,
    );
  }, [sidebarEntries, loadBucketAuditImages]);

  // Lazy-load snapshot images for unauthorized sidebar entries (including bucket frames)
  useEffect(() => {
    loadBucketAuditImages(
      unauthorizedSidebarEntries,
      unauthorizedSnapshotRequestsRef,
      setUnauthorizedSidebarEntries,
    );
  }, [unauthorizedSidebarEntries, loadBucketAuditImages]);

  // Fetch company settings on mount - gets active company (is_active=true) from database
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        // No company_id parameter - API returns the active company (is_active=true)
        const apiUrl = `${API_BASE_URL}/api/v1/settings/company`;
        console.log('[WebView] Fetching company settings from:', apiUrl);
        
        const response = await fetch(apiUrl, { headers: buildWebviewFetchHeaders() });
        console.log('[WebView] Company settings response status:', response.status);
        
        if (!response.ok) {
          console.error('[WebView] Failed to fetch company settings. Status:', response.status);
          return;
        }
        
        const data = await response.json();
        console.log('[WebView] Fetched active company settings:', data);
        
        // Use values from API, only fall back to defaults if truly empty/null/undefined
        const logoValue = resolveCompanyLogoSrc(data.logo, API_BASE_URL);
        const descriptionValue = data.description && data.description.trim() !== '' ? data.description : 'Welcome to Kivo.ai';
        
        setCompanySettings({
          logo: logoValue,
          description: descriptionValue
        });
      } catch (error) {
        console.error('[WebView] Error fetching company settings:', error);
        // Keep using defaults on error
      }
    };

    fetchCompanySettings();
  }, []);

  // Note: Access checking is now handled by the backend websocket.
  // When an admin disables intrusion access, the backend will stop sending events to this connection.
  // No periodic API calls needed - validation happens server-side in real-time when events are broadcast.

  // websocket connect (kept original, now pushes to showEntry)
  useEffect(() => {
    const connectWebsocket = () => {
      try {
        if (websocketRef.current) {
          websocketRef.current.close();
        }
        const socket = new WebSocket(deriveWebsocketUrl(resolvedLaneNumber, resolvedCameraIds));
        websocketRef.current = socket;

        socket.onopen = () => {
          setIsSocketConnected(true);
          console.log('[CUSTOM-WEBVIEW] intrusion ws connect', { filterIds: resolvedCameraIds });
        };

        socket.onmessage = (event) => {
          try {
            const payload = JSON.parse(event.data);
            console.log('[WebViewIntrusion] Received websocket event:', payload);

            if (isUnauthorizedRecognition(payload)) {
              const entry = buildDisplayEntry(payload, {
                mode: 'attendance',
                name: 'Unknown Person',
                isUnauthorized: true,
                auditLog: buildAuditLogFromPayload(payload, { isUnauthorized: true }),
              });
              if (!matchesLane(entry)) {
                return;
              }
              if (!matchesCamera(entry)) {
                return;
              }
              console.log('[WebViewIntrusion] Displaying unauthorized entry:', entry);
              showEntry(entry);
              return;
            }

            if (!isMatchedIntrusionEvent(payload)) {
              console.log('[WebViewIntrusion] Not a matched intrusion event, skipping');
              return;
            }
            const entry = buildDisplayEntry(payload, {
              mode: 'attendance',
              auditLog: buildAuditLogFromPayload(payload, { isUnauthorized: false }),
            });
            if (entry.mode !== 'attendance') {
              return;
            }
            if (!matchesLane(entry)) {
              return;
            }
            if (!matchesCamera(entry)) {
              return;
            }
            console.log('[WebViewIntrusion] Displaying intrusion entry:', entry);
            showEntry(entry);
          } catch (err) {
            console.error('Invalid websocket payload:', err);
          }
        };

        socket.onclose = () => {
          websocketRef.current = null;
          setIsSocketConnected(false);
          // Reconnect after delay - backend will validate access when events are broadcast
          // No need to check access here, backend handles it server-side
          if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
          }
          reconnectTimerRef.current = setTimeout(connectWebsocket, 4000);
        };

        socket.onerror = (err) => {
          console.error('Websocket error:', err);
          setIsSocketConnected(false);
          try { socket.close(); } catch (e) { }
        };
      } catch (error) {
        console.error('Failed to initialize websocket:', error);
      }
    };

    connectWebsocket();

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (websocketRef.current) {
        try { websocketRef.current.close(); } catch (e) { }
        websocketRef.current = null;
      }
      setIsSocketConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchesLane, matchesCamera, cameraFilterKey]);

  // Background styles
  const backgroundStyles = {
    position: 'fixed',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: `
      radial-gradient(circle at 20% 80%, rgba(64, 81, 138, 0.15) 0%, transparent 30%),
      radial-gradient(circle at 80% 20%, rgba(64, 81, 138, 0.10) 0%, transparent 30%),
      radial-gradient(circle at 40% 40%, rgba(64, 81, 138, 0.08) 0%, transparent 30%)
    `,
    animation: `${float} 20s ease-in-out infinite`,
    zIndex: -1,
  };

  // visibleSidebarEntries excludes any currently active on main screen
  const visibleSidebarEntries = sidebarEntries.filter(
    entry => !activeCards.some(c => c.id === entry.id)
  );
  const visibleUnauthorizedSidebarEntries = unauthorizedSidebarEntries.filter(
    (entry) => !activeCards.some((c) => c.id === entry.id)
  );
  const displayedSidebarEntries = visibleSidebarEntries.slice(0, blacklistedVisibleLimit);
  const displayedUnauthorizedSidebarEntries = visibleUnauthorizedSidebarEntries.slice(
    0,
    unauthorizedVisibleLimit
  );
  const canShowMoreBlacklisted =
    visibleSidebarEntries.length > blacklistedVisibleLimit || hasMoreLogs;
  const canShowMoreUnauthorized =
    visibleUnauthorizedSidebarEntries.length > unauthorizedVisibleLimit || unauthorizedHasMoreLogs;

  const sidebarPaperSx = {
    width: { xs: '100%', md: embedded ? '50%' : '23%' },
    minWidth: embedded ? 0 : { md: 320 },
    maxWidth: embedded ? 'none' : { md: 420 },
    flex: embedded ? 1 : undefined,
    height: { xs: 'auto', md: '100%' },
    minHeight: embedded ? { xs: 220, md: 0 } : undefined,
    maxHeight: { xs: embedded ? 'none' : '70vh', md: '100%' },
    borderRadius: { xs: 1.5, md: embedded ? 2 : 4 },
    background: 'linear-gradient(135deg, rgba(64, 81, 138, 0.95) 0%, rgba(90, 111, 168, 0.95) 100%)',
    backdropFilter: 'blur(15px)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
  };

  const sidebarHeaderSx = {
    flexShrink: 0,
    px: embedded ? { xs: 1.25, md: 1.75 } : 2.5,
    pt: embedded ? { xs: 1.25, md: 1.75 } : 2.5,
    pb: embedded ? 1 : 1.5,
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: '#ffffff',
  };

  const sidebarHeaderInnerSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    py: 0.85,
    px: 1.25,
    border: '1px solid rgba(198, 197, 210, 0.6)',
    borderRadius: 1.5,
    backgroundColor: '#ffffff',
    boxShadow: '0 2px 8px rgba(26, 34, 66, 0.08)',
  };

  const sidebarTitleSx = {
    fontWeight: 800,
    fontSize: embedded ? { xs: '0.85rem', md: '0.95rem' } : '1.05rem',
    letterSpacing: 0.2,
    lineHeight: 1.2,
    textAlign: 'center',
  };

  const sidebarScrollSx = {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    px: embedded ? { xs: 1.25, md: 1.75 } : 2.5,
    pt: embedded ? 1.25 : 2,
    pb: embedded ? 1.5 : 2.5,
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  };

  const renderSidebarAuditTile = (entry, tileOptions = {}) => {
    const log = entryToAuditLog(entry);
    const bucketLogs = entry.bucketLogs || log.bucket_logs || [];
    const logWithStack = {
      ...log,
      stack_size: entry.stackSize || log.stack_size || bucketLogs.length || 1,
    };
    const capturedTs = getCapturedTimestamp(logWithStack);
    const representativeAuditId = logWithStack.audit_id || entry.auditId;
    const bucketImage = collectBucketAuditIds(entry)
      .map((auditId) => imageCache[auditId])
      .find(Boolean);
    const image = entry.capturedImage
      || entry.image
      || bucketImage
      || (representativeAuditId ? imageCache[representativeAuditId] : undefined);
    const stackImages = buildSidebarStackImages(bucketLogs, logWithStack, imageCache)
      .filter((layer) => Boolean(layer?.image));
    const hidePersonName = tileOptions.hidePersonName
      || (tileOptions.hideUnknownPersonName && isGenericSidebarName(log.recognized_name));

    return (
      <Box key={entry.id} sx={{ minWidth: 0, width: '100%' }}>
        <AuditTile
          log={logWithStack}
          image={image}
          stackImages={stackImages}
          bucketLogs={bucketLogs}
          capturedTs={capturedTs}
          disableRecordingPlayback
          disableEmployeeLink
          hidePersonName={hidePersonName}
          hideTrackId={tileOptions.hideTrackId}
          hideFrameBadge
          hideIntrusionBadge={tileOptions.hideIntrusionBadge}
          statusLabel={tileOptions.statusLabel}
          statusChipSx={tileOptions.statusChipSx}
          onClick={() => openEntryTimingDialog(entry)}
        />
      </Box>
    );
  };

  const unauthorizedSidebarTileOptions = {
    hideTrackId: true,
    hideFrameBadge: true,
    statusLabel: 'Unmatched',
    statusChipSx: UNAUTHORIZED_STATUS_CHIP_SX,
  };

  const blacklistedSidebarTileOptions = {
    hideTrackId: true,
    hideFrameBadge: true,
    hideIntrusionBadge: true,
    hideUnknownPersonName: true,
    statusLabel: 'Blacklisted',
    statusChipSx: BLACKLISTED_STATUS_CHIP_SX,
  };

  const renderSidebarAuditGrid = (entries, tileOptions = {}) => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 1,
        alignItems: 'stretch',
      }}
    >
      {entries.map((entry) => renderSidebarAuditTile(entry, tileOptions))}
    </Box>
  );

  const renderSidebarSkeletonGrid = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 1,
        alignItems: 'stretch',
      }}
    >
      {Array.from({ length: SIDEBAR_PAGE_SIZE }).map((_, idx) => (
        <Box key={`sidebar-skel-${idx}`} sx={{ minWidth: 0, width: '100%' }}>
          <SidebarTileSkeleton />
        </Box>
      ))}
    </Box>
  );

  const resolveEventTileOptions = (entry) => (
    entry?.isUnauthorized ? unauthorizedSidebarTileOptions : blacklistedSidebarTileOptions
  );

  const renderMainEventTile = (entry) =>
    renderSidebarAuditTile(entry, resolveEventTileOptions(entry));

  const renderMainEventGrid = (entries) => {
    const shown = entries.slice(0, 4);
    const count = shown.length;
    const columns = count === 1 ? 1 : 2;

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          gap: 2,
          width: '100%',
          maxWidth: count === 1 ? 320 : { xs: '100%', sm: 680 },
          mx: 'auto',
          px: { xs: 1.5, md: 3 },
          py: 1,
          alignItems: 'stretch',
        }}
      >
        {shown.map((entry) => renderMainEventTile(entry))}
      </Box>
    );
  };

  const renderIntrusionSidebar = (title, titleColor, HeaderIcon, content) => (
    <Paper elevation={8} sx={sidebarPaperSx}>
      <Box sx={sidebarHeaderSx}>
        <Box sx={sidebarHeaderInnerSx}>
          {HeaderIcon ? (
            <HeaderIcon sx={{ color: titleColor, fontSize: '1.35rem', flexShrink: 0 }} />
          ) : null}
          <Typography sx={{ ...sidebarTitleSx, color: titleColor }}>
            {title}
          </Typography>
        </Box>
      </Box>
      <Box sx={sidebarScrollSx}>
        <Stack spacing={2.5}>
          {content}
        </Stack>
      </Box>
    </Paper>
  );

  // Main render
  return (
    <Box sx={{
      minHeight: embedded ? '100%' : '100vh',
      height: embedded ? '100%' : 'auto',
      '@supports (height: 100dvh)': {
        minHeight: embedded ? '100%' : '100dvh',
      },
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      color: '#40518A',
      position: 'relative',
      overflowX: 'hidden',
      overflowY: { xs: 'auto', md: 'hidden' },
      '&::before': backgroundStyles,
      '&::after': {
        content: '""',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 50% 50%, rgba(64, 81, 138, 0.1) 0%, transparent 70%)',
        pointerEvents: 'none',
        zIndex: 0
      }
    }}>
      {/* Main Container */}
      <Container
        maxWidth={embedded ? false : 'xl'}
        disableGutters={embedded}
        sx={{
          minHeight: embedded ? '100%' : '100vh',
          height: embedded ? '100%' : { xs: 'auto', md: '100vh' },
          '@supports (height: 100dvh)': {
            minHeight: embedded ? '100%' : '100dvh',
            height: embedded ? '100%' : { xs: 'auto', md: '100dvh' },
          },
          py: embedded ? 0 : { xs: 1, sm: 1.5, md: 2 },
          px: embedded ? 0 : undefined,
          position: 'relative',
          zIndex: 1
        }}
      >
        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={embedded ? { xs: 0.75, md: 1 } : 2}
          sx={{ height: { xs: embedded ? '100%' : 'auto', md: '100%' } }}
        >
          {/* Main Display Area */}
          <Paper
            elevation={24}
            sx={{
              display: embedded ? 'none' : 'flex',
              flex: 1,
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: 4,
              p: 2,
              position: 'relative',
              transition: 'all 0.5s ease',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(64, 81, 138, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(64, 81, 138, 0.03) 0%, transparent 50%)',
                zIndex: 0,
              }
            }}
          >
            {/* Socket Connection Indicator - Inside Main Display Area */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 20,
                right: 20,
                width: 45,
                height: 45,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                zIndex: 1000,
                border: '2px solid rgba(64, 81, 138, 0.1)',
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  color: isSocketConnected ? '#2ecc71' : '#e74c3c',
                  backgroundColor: 'currentColor',
                  animation: `${connectionPulse} 1.8s ease-out infinite`,
                }}
                aria-label={isSocketConnected ? 'Socket connected' : 'Socket disconnected'}
              />
            </Box>
            <Box sx={{ position: 'relative', zIndex: 1, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              {/* Page Header with Logo - Shows when cards are present */}
              {(activeCards.length > 0 || currentEntry) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    p: 1.5,
                    borderRadius: 2,
                    backdropFilter: 'blur(10px)',
                    animation: `${pulse} 2s infinite`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    bgcolor: 'rgba(255,255,255,0.9)',
                  }}
                >
                  <Box
                    component="img"
                    src={companySettings.logo}
                    alt="Company Logo"
                    sx={{
                      height: 35,
                      width: 'auto',
                      borderRadius: 1,
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              )}

              {/* Dynamic Attendance Cards Rendering */}
              {(() => {
                // Reverse the cards so newest (last added) appears first
                const cards = activeCards.map(c => c.original || c).reverse();
                const count = cards.length;

                // 0 cards → Welcome
                if (count === 0) {
                  return (
                    <Box
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >

                      {/* Always Visible Logo in Top-Left */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 20,
                          left: 20,
                          p: 1.5,
                          
                        }}
                      >
                        <Box
                          component="img"
                          src={companySettings.logo}
                          alt="Company Logo"
                          sx={{
                            height: 60,
                            width: 'auto',
                            borderRadius: 1,
                            objectFit: 'contain'
                          }}
                        />
                      </Box>

                      {/* Welcome Message */}
                      <Box
                        sx={{
                          textAlign: 'center',
                          animation: `${pulse} 2s infinite`,
                          px: 4
                        }}
                      >
                        <Typography
                          variant="h1"
                          sx={{
                            fontSize: { xs: '2.5rem', md: '3.8rem' },
                            color: '#40518A',
                            mb: 2,
                            textShadow: '0 3px 15px rgba(64, 81, 138, 0.2)',
                            fontWeight: '900',
                            whiteSpace: 'pre-line',
                            letterSpacing: 1
                          }}
                        >
                          {companySettings.description}
                        </Typography>
                      </Box>

                    </Box>
                  );
                }

                return renderMainEventGrid(cards);
              })()}
            </Box>
          </Paper>

          {renderIntrusionSidebar('Unauthorized Person', '#FF2C2C', DoDisturbAltIcon, (
            <>
              {initialUnauthorizedLogsLoading ? (
                renderSidebarSkeletonGrid()
              ) : displayedUnauthorizedSidebarEntries.length > 0 ? (
                renderSidebarAuditGrid(displayedUnauthorizedSidebarEntries, unauthorizedSidebarTileOptions)
              ) : (
                <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{ color: 'white', fontSize: '1.15rem', fontWeight: 600, opacity: 0.9, mb: 1 }}
                  >
                    No Unauthorized Activity
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem' }}>
                    Unmatched detections will appear here
                  </Typography>
                </Box>
              )}
              {canShowMoreUnauthorized && visibleUnauthorizedSidebarEntries.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5, pb: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handleLoadMoreUnauthorized}
                    disabled={unauthorizedLoadingMoreLogs}
                    sx={{
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.55)',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2.5,
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(255, 255, 255, 0.55)',
                        borderColor: 'rgba(255, 255, 255, 0.35)',
                      },
                    }}
                  >
                    {unauthorizedLoadingMoreLogs ? (
                      <CircularProgress size={18} sx={{ color: 'white' }} />
                    ) : (
                      'Show More'
                    )}
                  </Button>
                </Box>
              )}
            </>
          ))}

          {renderIntrusionSidebar('Blacklisted Person', '#40518A', PersonOffOutlinedIcon, (
            <>
              {initialBlacklistedLogsLoading ? (
                renderSidebarSkeletonGrid()
              ) : displayedSidebarEntries.length > 0 ? (
                renderSidebarAuditGrid(displayedSidebarEntries, blacklistedSidebarTileOptions)
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 8,
                  px: 2
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'white', 
                      fontSize: '1.15rem',
                      fontWeight: 600,
                      opacity: 0.9,
                      mb: 1
                    }}
                  >
                    No Recent Activity
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255,255,255,0.7)', 
                      fontSize: '0.95rem'
                    }}
                  >
                    Intrusion logs will appear here
                  </Typography>
                </Box>
              )}
              {canShowMoreBlacklisted && visibleSidebarEntries.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5, pb: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handleLoadMoreBlacklisted}
                    disabled={loadingMoreLogs}
                    sx={{
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.55)',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2.5,
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(255, 255, 255, 0.55)',
                        borderColor: 'rgba(255, 255, 255, 0.35)',
                      },
                    }}
                  >
                    {loadingMoreLogs ? (
                      <CircularProgress size={18} sx={{ color: 'white' }} />
                    ) : (
                      'Show More'
                    )}
                  </Button>
                </Box>
              )}
            </>
          ))}
        </Stack>
      </Container>

      <AuditProcessingTimelineModal
        timingDialog={timingDialog}
        closeTimingDialog={closeTimingDialog}
        isMobile={isMobile}
        timingDialogResultMeta={timingDialogResultMeta}
        hasTimingDialogTrackAttendance={false}
        modalFrames={Array.isArray(timingDialog.stack) ? timingDialog.stack : []}
        imageCache={imageCache}
        showPossibleMatchActions={false}
        showProcessingTimeline
        showTimingHeading
        showTopMatches={false}
        frameStatusLabel={
          isBlacklistedTimingDialog
            ? 'Blacklisted'
            : (isUnauthorizedTimingDialog ? 'Unmatched' : null)
        }
        frameStatusChipSx={
          isBlacklistedTimingDialog
            ? BLACKLISTED_STATUS_CHIP_SX
            : (isUnauthorizedTimingDialog ? UNAUTHORIZED_STATUS_CHIP_SX : null)
        }
      />

    </Box>
  );
};

export default WebViewIntrusion;
